// AGC: UsageTimeline - Displays chronological list of machine work logs
// AGC: Groups by date (today, yesterday, this week, older)
// AGC: Shows employee, lot, hours used

import React, { useMemo } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import type { MachineWorkLog } from '../../types/machineWorkLog'
import { Clock, MapPin, User, Calendar, ChevronRight, Fuel, Loader2 } from 'lucide-react'

interface UsageTimelineProps {
  workLogs: MachineWorkLog[]
  isLoading?: boolean
  onLotClick?: (lotId: string) => void
  maxItems?: number
  showViewAll?: boolean
  onViewAll?: () => void
}

/**
 * AGC: Date grouping helper
 * AGC: Groups work logs by relative date categories
 */
const getDateGroup = (dateString: string): 'today' | 'yesterday' | 'thisWeek' | 'older' => {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const weekAgo = new Date(today)
  weekAgo.setDate(weekAgo.getDate() - 7)

  // AGC: Reset time for comparison
  today.setHours(0, 0, 0, 0)
  yesterday.setHours(0, 0, 0, 0)
  weekAgo.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)

  if (date.getTime() === today.getTime()) return 'today'
  if (date.getTime() === yesterday.getTime()) return 'yesterday'
  if (date.getTime() >= weekAgo.getTime()) return 'thisWeek'
  return 'older'
}

/**
 * AGC: Format hours for display
 */
const formatHours = (hours: number): string => {
  return `${hours.toFixed(1)}h`
}

/**
 * AGC: Format date for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  })
}

/**
 * AGC: Single work log entry component
 */
const WorkLogEntry: React.FC<{
  log: MachineWorkLog
  onLotClick?: (lotId: string) => void
  t: (key: string) => string
}> = ({ log, onLotClick, t }) => {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
      {/* AGC: Timeline dot */}
      <div className="flex-shrink-0 mt-1">
        <div className="w-3 h-3 rounded-full bg-emerald-500" />
      </div>

      {/* AGC: Content */}
      <div className="flex-1 min-w-0">
        {/* AGC: Top row - Lot and hours */}
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={() => log.lotId && onLotClick?.(log.lotId)}
            className={`text-sm font-medium text-gray-900 dark:text-white truncate ${
              log.lotId ? 'hover:text-emerald-600 dark:hover:text-emerald-400 cursor-pointer' : ''
            }`}
            disabled={!log.lotId}
          >
            {log.lotName || t('machines.detail.generalOperation')}
          </button>
          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 ml-2">
            {formatHours(log.hoursUsed)}
          </span>
        </div>

        {/* AGC: Operator info */}
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          {log.operatorAvatar ? (
            <img
              src={log.operatorAvatar}
              alt={log.operatorName}
              className="w-4 h-4 rounded-full"
            />
          ) : (
            <User className="w-3 h-3" />
          )}
          <span>{log.operatorName}</span>
          <span className="text-gray-400">•</span>
          <span>{formatDate(log.date)}</span>
        </div>

        {/* AGC: Additional metrics */}
        {(log.hectaresWorked || log.fuelUsed) && (
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
            {log.hectaresWorked && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {log.hectaresWorked.toFixed(1)} ha
              </span>
            )}
            {log.fuelUsed && (
              <span className="flex items-center gap-1">
                <Fuel className="w-3 h-3" />
                {log.fuelUsed.toFixed(1)} L
              </span>
            )}
          </div>
        )}

        {/* AGC: Notes */}
        {log.notes && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
            {log.notes}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * AGC: UsageTimeline component
 */
export const UsageTimeline: React.FC<UsageTimelineProps> = ({
  workLogs,
  isLoading = false,
  onLotClick,
  maxItems,
  showViewAll = false,
  onViewAll,
}) => {
  const { t } = useTranslation()

  // AGC: Group logs by date category
  const groupedLogs = useMemo(() => {
    const groups: Record<string, MachineWorkLog[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    }

    // AGC: Limit items if maxItems is set
    const logsToProcess = maxItems ? workLogs.slice(0, maxItems) : workLogs

    logsToProcess.forEach((log) => {
      const group = getDateGroup(log.date)
      groups[group].push(log)
    })

    return groups
  }, [workLogs, maxItems])

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  // AGC: Empty state
  if (workLogs.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
        <p className="text-gray-600 dark:text-gray-400 font-medium">
          {t('machines.detail.noUsageLogs')}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
          {t('machines.detail.noUsageLogsDescription')}
        </p>
      </div>
    )
  }

  // AGC: Render date group with logs
  const renderGroup = (
    groupKey: 'today' | 'yesterday' | 'thisWeek' | 'older',
    logs: MachineWorkLog[]
  ) => {
    if (logs.length === 0) return null

    const groupLabels: Record<string, string> = {
      today: t('machines.detail.today'),
      yesterday: t('machines.detail.yesterday'),
      thisWeek: t('machines.detail.thisWeek'),
      older: t('machines.detail.older'),
    }

    return (
      <div key={groupKey} className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {groupLabels[groupKey]}
        </h4>
        <div className="space-y-2">
          {logs.map((log) => (
            <WorkLogEntry
              key={log.id}
              log={log}
              onLotClick={onLotClick}
              t={t}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {renderGroup('today', groupedLogs.today)}
      {renderGroup('yesterday', groupedLogs.yesterday)}
      {renderGroup('thisWeek', groupedLogs.thisWeek)}
      {renderGroup('older', groupedLogs.older)}

      {/* AGC: View all button */}
      {showViewAll && workLogs.length > (maxItems || 0) && onViewAll && (
        <button
          onClick={onViewAll}
          className="w-full py-2 text-sm text-emerald-600 dark:text-emerald-400 font-medium hover:underline flex items-center justify-center gap-1"
        >
          {t('machines.detail.viewAllLogs')} ({workLogs.length})
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

export default UsageTimeline
